import { css, html, nothing } from "lit";
import { ifDefined } from "lit-html/directives/if-defined.js";
import { BaseComponent } from "../../BaseComponent.js";
import { defineElement } from "../../../decorators/defineElement.js";
import { prop } from "../../../decorators/prop/index.js";
import { getClasses } from "./utils.js";
import "../../atom/Icon/index.js";
import "../../atom/Text/index.js";
import "../../atom/ClickOutside/index.js";
import "../../atom/FocusTrap/index.js";
import "../../atom/Count/index.js";
import "../Tooltip/index.js";
import caretUp from "../../../assets/icon/caret-up.svg.js";
import caretDown from "../../../assets/icon/caret-down.svg.js";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
let DsfNavbarItem = class extends BaseComponent {
  constructor() {
    super(...arguments);
    this.dsf_selected = false;
    this.dsf_always_open = false;
    this.dsf_light = false;
    this.dsf_notification_count = 0;
    this.dsf_open = false;
    this.dsf_collapsed = false;
    this.dsf_submenu = false;
  }
  get hasContent() {
    return this.childElementCount > 0;
  }
  get isNestedItem() {
    return this.parentElement instanceof DsfNavbarItem;
  }
  get hasSelectedChild() {
    return [...this.children].some((c) => c.dsf_selected);
  }
  closeMenu() {
    this.dsf_open = false;
  }
  /* Lifecycle methods */
  connectedCallback() {
    super.connectedCallback();
    this.onEscape = this.onEscape.bind(this);
    document.addEventListener("keydown", this.onEscape);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("keydown", this.onEscape);
  }
  async updated(_changedProperties) {
    super.updated(_changedProperties);
    await this.onUpdate(_changedProperties);
  }
  async onUpdate(_changedProperties) {
    await this.updateComplete;
    if (!this.isNestedItem && this.hasContent) {
      if (this.dsf_collapsed) {
        this.dsf_selected = this.hasSelectedChild;
      } else {
        this.dsf_selected = false;
        if (!_changedProperties.has("dsf_open")) {
          this.dsf_open = this.dsf_open || this.hasSelectedChild;
        }
      }
    }
    if (this.isNestedItem && _changedProperties.has("dsf_selected")) {
      const parentItem = this.parentElement;
      parentItem.requestUpdate();
    }
  }
  /* Event listeners */
  onClick(event) {
    if (this.disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    if (this.dsf_href) {
      if (!event.metaKey && !event.ctrlKey) {
        event.preventDefault();
        this.emit({ name: "navigate", payload: this.dsf_id });
      }
    } else if (!this.dsf_always_open || this.dsf_always_open && this.dsf_collapsed) {
      if (this.hasContent) {
        this.dsf_open = !this.dsf_open;
        this.emit(
          { name: this.dsf_open ? "expand" : "collapse", payload: this.dsf_id },
          { bubbles: false }
        );
      } else {
        this.emit({ name: "navigate", payload: this.dsf_id });
      }
    }
  }
  onMouseEnter() {
    if (this.hasContent && this.dsf_collapsed && !this.dsf_open) {
      this.dsf_open = true;
    }
  }
  onMouseOutside() {
    this.closeMenu();
  }
  onContextMenu(event) {
    if (this.disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
  onChildNavigate() {
    this.dsf_selected = true;
    this.closeMenu();
  }
  onEscape(e) {
    if (e.key === "Escape") {
      this.closeMenu();
    }
  }
  /* Rendering functions */
  renderCollapseIcon() {
    const collapseIconName = this.dsf_open ? caretUp : caretDown;
    return !this.dsf_collapsed && !this.isNestedItem && this.hasContent && !this.dsf_always_open ? html`<dsf-icon
          class="h-4 w-8 items-center"
          dsf_name=${collapseIconName}
          dsf_accessibility_label="Collapse"
        ></dsf-icon>` : nothing;
  }
  get isCtxItem() {
    return this.dsf_collapsed && this.isNestedItem;
  }
  renderTitle() {
    if (!this.dsf_collapsed || this.isCtxItem) {
      const level = this.isCtxItem || this.dsf_light ? "s" : this.isNestedItem ? this.dsf_submenu ? "xxs" : "xs" : "xs-semibold";
      const intencity = this.dsf_selected && !this.isNestedItem ? "white" : this.disabled ? 300 : 600;
      return html` <dsf-text
        dsf_level=${level}
        dsf_color="neutral"
        dsf_color_intensity=${intencity}
      >
        ${this.dsf_title}
      </dsf-text>`;
    }
    return nothing;
  }
  renderChildren() {
    return !this.dsf_collapsed && (this.dsf_open || this.dsf_always_open) ? html` <div class="dsf-pt-xs flex flex-col gap-4">
          <slot class="gap-4"></slot>
        </div>` : nothing;
  }
  ctxKeydown(e) {
    var _a, _b;
    if (e.key === "ArrowUp") {
      try {
        const el = e.target;
        (_a = el == null ? void 0 : el.previousElementSibling) == null ? void 0 : _a.focus();
      } catch (e2) {
      }
    }
    if (e.key === "ArrowDown") {
      try {
        const el = e.target;
        (_b = el == null ? void 0 : el.nextElementSibling) == null ? void 0 : _b.focus();
      } catch (e2) {
      }
    }
  }
  renderIcon() {
    if (this.isNestedItem) {
      if (this.dsf_collapsed || this.dsf_submenu) {
        return nothing;
      }
      if (!this.dsf_selected) {
        return html`<div class="w-16"></div>`;
      }
      return html`<dsf-icon part="icon" dsf_name="Dot" class="h-16 w-16"></dsf-icon>`;
    }
    return html`<dsf-icon
      part="icon"
      class="h-16 w-16"
      dsf_name=${ifDefined(this.dsf_icon)}
    ></dsf-icon>`;
  }
  renderNotificationCount() {
    return this.dsf_notification_count > 0 ? html`<dsf-count dsf_count=${this.dsf_notification_count}></dsf-count>` : nothing;
  }
  render() {
    const tabIndex = this.dsf_always_open && !this.dsf_collapsed || this.disabled ? -1 : 0;
    const classes = getClasses(this);
    const notificationCount = this.renderNotificationCount();
    const collapseIcon = this.renderCollapseIcon();
    const navbarContent = html`
      ${this.renderIcon()} ${this.renderTitle()}
      ${notificationCount !== nothing || collapseIcon !== nothing ? html`<div class="ml-auto flex items-center gap-4">
            ${notificationCount} ${collapseIcon}
          </div>` : nothing}
    `;
    const baseComponent = this.dsf_href ? html`
          <a
            tabindex=${tabIndex}
            class=${classes}
            title=${ifDefined(this.dsf_title)}
            href="${this.dsf_href}"
            @click=${this.onClick}
            @contextmenu=${this.onContextMenu}
            @mouseenter=${this.onMouseEnter}
            ?disabled=${this.disabled}
          >
            ${navbarContent}
          </a>
        ` : html`
          <button
            tabindex=${tabIndex}
            class=${classes}
            title=${ifDefined(this.dsf_title)}
            @click=${this.onClick}
            @mouseenter=${this.onMouseEnter}
            ?disabled=${this.disabled}
          >
            ${navbarContent}
          </button>
        `;
    const navbarItem = this.hasContent && this.dsf_collapsed && this.dsf_open ? (
      // navbar item with submenu
      html` <dsf-click-outside
            slot="anchor"
            @click-outside=${this.closeMenu}
            @mouse-outside=${this.onMouseOutside}
          >
            <dsf-dropdown
              dsf_placement="right-start"
              ?dsf_open=${this.dsf_open}
              dsf_hover_bridge
              dsf_offset="4"
            >
              <div slot="anchor">${baseComponent}</div>
              <div
                class="flex w-max min-w-180 flex-col gap-4 rounded-m border border-neutral-100 bg-neutral-0"
              >
                <dsf-text dsf_level="s" dsf_color_intensity="300" class="dsf-px-s dsf-pt-s"
                  >${this.dsf_title}</dsf-text
                >
                <dsf-focus-trap dsf_focus_selector="button">
                  <slot @navigate=${this.onChildNavigate} @keydown=${this.ctxKeydown}></slot>
                </dsf-focus-trap>
              </div>
            </dsf-dropdown>
          </dsf-click-outside>`
    ) : (
      // normal navbar item
      html`<div class="relative grow" slot="anchor">
            ${baseComponent} ${this.renderChildren()}
          </div>`
    );
    if (this.dsf_collapsed && !this.isNestedItem && !this.dsf_open) {
      return html`
        <dsf-tooltip dsf_title=${ifDefined(this.dsf_title)} dsf_placement="right">
          ${navbarItem}
        </dsf-tooltip>
      `;
    }
    return navbarItem;
  }
};
DsfNavbarItem.styles = [
  ...BaseComponent.styles,
  css`
      dsf-dropdown::part(dropdown) {
        z-index: var(--dsf-z-index-dropdown);
      }
    `
];
__decorateClass([
  prop({ type: String })
], DsfNavbarItem.prototype, "dsf_id", 2);
__decorateClass([
  prop({ type: String })
], DsfNavbarItem.prototype, "dsf_title", 2);
__decorateClass([
  prop({ type: String })
], DsfNavbarItem.prototype, "dsf_icon", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfNavbarItem.prototype, "dsf_selected", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfNavbarItem.prototype, "dsf_always_open", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfNavbarItem.prototype, "dsf_light", 2);
__decorateClass([
  prop({ type: Number })
], DsfNavbarItem.prototype, "dsf_notification_count", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfNavbarItem.prototype, "disabled", 2);
__decorateClass([
  prop({ type: String })
], DsfNavbarItem.prototype, "dsf_href", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfNavbarItem.prototype, "dsf_open", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfNavbarItem.prototype, "dsf_collapsed", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfNavbarItem.prototype, "dsf_submenu", 2);
DsfNavbarItem = __decorateClass([
  defineElement("dsf-navbar-item")
], DsfNavbarItem);
export {
  DsfNavbarItem
};
